import { escapeCsvCell } from './csvEscape';
import type { InventoryRow } from 'src/admin/models/PoolManagementModel';

export interface ParsedInventoryRow {
    /** 1-based data row index, ignoring the header row. */
    rowIndex: number;
    symbol: string;
    quantity: number;
    /** null when the LAB Price column is absent or empty for this row. */
    labPrice: number | null;
}

export interface InventoryParseError {
    /** 1-based data row index (header errors use 0). */
    rowIndex: number;
    symbol: string | null;
    message: string;
}

export interface InventoryParseResult {
    rows: ParsedInventoryRow[];
    errors: InventoryParseError[];
}

const SYMBOL_HEADER = 'symbol';
const QUANTITY_HEADER = 'quantity';
const LAB_PRICE_HEADER = 'lab price';

function splitCsvLine(line: string): string[] {
    const cells: string[] = [];
    let i = 0;
    let current = '';
    let inQuotes = false;
    while (i < line.length) {
        const ch = line[i]!;
        if (inQuotes) {
            if (ch === '"') {
                if (line[i + 1] === '"') {
                    current += '"';
                    i += 2;
                    continue;
                }
                inQuotes = false;
                i += 1;
                continue;
            }
            current += ch;
            i += 1;
            continue;
        }
        if (ch === '"') {
            inQuotes = true;
            i += 1;
            continue;
        }
        if (ch === ',') {
            cells.push(current);
            current = '';
            i += 1;
            continue;
        }
        current += ch;
        i += 1;
    }
    cells.push(current);
    return cells;
}

function normalizeHeader(value: string): string {
    return value.replace(/^﻿/, '').trim().toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ');
}

/**
 * Phase-1 CSV parser for Pool Management uploads. Reads a header row plus
 * data rows and validates Symbol, Quantity, and optionally LAB Price.
 *
 * Headers are matched case-insensitively, with `_` treated as space (so both
 * "LAB Price" and "lab_price" are accepted).
 */
export function parseInventoryCsv(text: string): InventoryParseResult {
    const result: InventoryParseResult = { rows: [], errors: [] };

    const lines = text.replace(/^﻿/, '').split(/\r\n|\n|\r/);
    while (lines.length > 0 && lines[lines.length - 1]!.trim() === '') lines.pop();

    if (lines.length === 0) {
        result.errors.push({ rowIndex: 0, symbol: null, message: 'File is empty.' });
        return result;
    }

    const headerCells = splitCsvLine(lines[0]!).map(normalizeHeader);
    const symbolIdx = headerCells.indexOf(SYMBOL_HEADER);
    const quantityIdx = headerCells.indexOf(QUANTITY_HEADER);
    const labPriceIdx = headerCells.indexOf(LAB_PRICE_HEADER);

    if (symbolIdx === -1) {
        result.errors.push({ rowIndex: 0, symbol: null, message: 'Missing required column "Symbol".' });
    }
    if (quantityIdx === -1) {
        result.errors.push({ rowIndex: 0, symbol: null, message: 'Missing required column "Quantity".' });
    }
    if (symbolIdx === -1 || quantityIdx === -1) return result;

    const seenSymbols = new Map<string, number>();

    for (let i = 1; i < lines.length; i += 1) {
        const line = lines[i]!;
        const rowIndex = i;
        if (line.trim() === '') continue;
        const cells = splitCsvLine(line);

        const rawSymbol = (cells[symbolIdx] ?? '').trim();
        if (!rawSymbol) {
            result.errors.push({
                rowIndex,
                symbol: null,
                message: 'Row ' + rowIndex + ': missing required column "Symbol".',
            });
            continue;
        }
        const symbol = rawSymbol.toUpperCase();

        const rawQuantity = (cells[quantityIdx] ?? '').trim();
        const quantity = Number(rawQuantity);
        if (rawQuantity === '' || !Number.isFinite(quantity) || !Number.isInteger(quantity) || quantity < 0) {
            result.errors.push({
                rowIndex,
                symbol,
                message:
                    'Row ' + rowIndex + ': invalid quantity (' + rawQuantity + ') — must be a non-negative integer.',
            });
            continue;
        }

        let labPrice: number | null = null;
        if (labPriceIdx !== -1) {
            const rawPrice = (cells[labPriceIdx] ?? '').trim();
            if (rawPrice !== '') {
                const parsedPrice = Number(rawPrice);
                if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
                    result.errors.push({
                        rowIndex,
                        symbol,
                        message:
                            'Row ' +
                            rowIndex +
                            ': invalid LAB Price (' +
                            rawPrice +
                            ') — must be a non-negative decimal.',
                    });
                    continue;
                }
                labPrice = parsedPrice;
            }
        }

        const duplicateOf = seenSymbols.get(symbol);
        if (duplicateOf !== undefined) {
            result.errors.push({
                rowIndex,
                symbol,
                message: 'Row ' + rowIndex + ': duplicate of row ' + duplicateOf + '.',
            });
            continue;
        }
        seenSymbols.set(symbol, rowIndex);

        result.rows.push({ rowIndex, symbol, quantity, labPrice });
    }

    return result;
}

/**
 * Header for the upload template and the parser's expected shape. Kept
 * as the minimal round-trippable column set (Symbol + Quantity + optional
 * LAB Price) so a user can fill in the template and upload it.
 */
const INVENTORY_CSV_HEADER = 'Symbol,Quantity,LAB Price';

/**
 * Build a downloadable phase-1 template CSV. Includes both required columns
 * and the optional LAB Price column with a couple of illustrative rows.
 */
export function createInventoryTemplateCsv(): string {
    return [INVENTORY_CSV_HEADER, 'AAPL,10000,0.0025', 'MSFT,8500,0.0028'].join('\n') + '\n';
}

/**
 * Column order for the pool export, mirroring the on-screen inventory
 * table (see `InventoryTable.svelte`) so the download matches what the
 * user sees. This is intentionally wider than {@link INVENTORY_CSV_HEADER}:
 * the export is a snapshot/report, not a re-upload source, so it carries
 * read-only columns (borrow status, prices, revenue) the parser ignores.
 */
const POOL_EXPORT_CSV_HEADER =
    'Symbol,Firm Borrow Status,Quantity Used,Current Quantity,Current Price,Fair Value,Current LAB Price,Daily Revenue';

/** The inventory-row fields the pool export serializes, in table order. */
type PoolExportRow = Pick<
    InventoryRow,
    | 'symbol'
    | 'borrowStatus'
    | 'quantityUsed'
    | 'currentQuantity'
    | 'currentPrice'
    | 'fairValuePrice'
    | 'currentLabPrice'
    | 'dailyRevenue'
>;

// Cell-escape lives in `./csvEscape` so the change-log exporter and
// any future CSV writers share one source of truth for round-trip
// normalization and formula-injection mitigations.

/**
 * Serialize the current pool inventory to CSV, one column per visible
 * table column and in the same order (see {@link POOL_EXPORT_CSV_HEADER}).
 * Nullable columns (borrow status, current/fair-value prices, quantity
 * used, daily revenue) serialize to an empty cell — the table renders
 * these as an em dash, but a blank is the machine-readable equivalent.
 * Cells are escaped per RFC 4180 for `,` and `"`; embedded CR/LF are
 * normalized to spaces by {@link escapeCsvCell}.
 */
export function createInventoryCsv(rows: ReadonlyArray<PoolExportRow>): string {
    const lines = [POOL_EXPORT_CSV_HEADER];
    for (const row of rows) {
        lines.push(
            [
                row.symbol,
                row.borrowStatus ?? '',
                row.quantityUsed ?? '',
                row.currentQuantity,
                row.currentPrice ?? '',
                row.fairValuePrice ?? '',
                row.currentLabPrice,
                row.dailyRevenue ?? '',
            ]
                .map(escapeCsvCell)
                .join(','),
        );
    }
    return lines.join('\n') + '\n';
}
